import { SignedDecimalStringSchema } from "./decimal-string";
import { z } from "zod";

// Live, redacted view of an opponent's trading in an Arena competition: no pair, side, size or price until the reveal.

/** `opened` new, `added` increase, `closed` reduce or close (incl. elimination force-close), `liquidated` liquidation. */
export const OpponentActivityKindSchema = z.enum(["opened", "added", "closed", "liquidated"]);
export type OpponentActivityKind = z.infer<typeof OpponentActivityKindSchema>;

const OpponentActivityEventBase = {
  /** `<paper fill id>:<leg>`: the same event on the live stream and in the on-load read (a flip's legs are 0 and 1). */
  id: z.string().min(1),
  hubUserId: z.number().int(),
  at: z.iso.datetime(),
};

/** A position opened or increased: the leverage it holds, and whether that is the pair's effective cap. */
const OpponentOpenEventFields = {
  ...OpponentActivityEventBase,
  leverage: z.number().int().positive(),
  atMaxLeverage: z.boolean(),
  realizedPnlUsd: z.null(),
  partial: z.null(),
};

/** Every field on every kind, null where the kind has none; `kind` decides which are set, so a parse rejects the rest. */
export const OpponentActivityEventSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("opened"), ...OpponentOpenEventFields }),
  z.object({ kind: z.literal("added"), ...OpponentOpenEventFields }),
  z.object({
    kind: z.literal("closed"),
    ...OpponentActivityEventBase,
    leverage: z.null(),
    atMaxLeverage: z.null(),
    /** In cents. */
    realizedPnlUsd: SignedDecimalStringSchema,
    /** Part of the same position is still open (a flip's close is not partial). */
    partial: z.boolean(),
  }),
  z.object({
    kind: z.literal("liquidated"),
    ...OpponentActivityEventBase,
    leverage: z.null(),
    atMaxLeverage: z.null(),
    /** In cents. */
    realizedPnlUsd: SignedDecimalStringSchema,
    partial: z.null(),
  }),
]);
export type OpponentActivityEventDto = z.infer<typeof OpponentActivityEventSchema>;

/**
 * The player's open exposure: their highest-leverage open position (ties: the
 * earliest opened). `null` = flat. Multi-market players hold several; one is
 * shown, so a close on one market never reads as flat while another is open.
 */
export const OpponentOpenPositionSchema = z.object({
  leverage: z.number().int().positive(),
  atMaxLeverage: z.boolean(),
  openedAt: z.iso.datetime(),
});
export type OpponentOpenPositionDto = z.infer<typeof OpponentOpenPositionSchema>;

/** One fill's worth of activity on the room stream. A flip carries `closed` then `opened`. */
export const OpponentActivitySsePayloadSchema = z.object({
  hubUserId: z.number().int(),
  /** Chronological. */
  activities: z.array(OpponentActivityEventSchema).min(1),
  /** The player's open exposure after this fill. */
  open: OpponentOpenPositionSchema.nullable(),
});
export type OpponentActivitySsePayloadDto = z.infer<typeof OpponentActivitySsePayloadSchema>;

export const OPPONENT_ACTIVITY_LAST_ACTIONS_LIMIT = 5;

export const OpponentActivityPlayerStateSchema = z.object({
  hubUserId: z.number().int(),
  open: OpponentOpenPositionSchema.nullable(),
  /** Newest first. */
  lastActions: z.array(OpponentActivityEventSchema).max(OPPONENT_ACTIVITY_LAST_ACTIONS_LIMIT),
  stats: z.object({
    /** Positions opened (a flip opens one). */
    trades: z.number().int().nonnegative(),
    /** Mean leverage of the positions opened; null before the first. */
    avgLeverage: z.number().positive().nullable(),
  }),
});
export type OpponentActivityPlayerStateDto = z.infer<typeof OpponentActivityPlayerStateSchema>;

/** A player whose state could not be rebuilt server-side: the board shows "—" rather than guessed stats. */
export const OpponentActivityUnavailablePlayerSchema = z.object({
  hubUserId: z.number().int(),
  unavailable: z.literal(true),
});
export type OpponentActivityUnavailablePlayerDto = z.infer<typeof OpponentActivityUnavailablePlayerSchema>;

export const isOpponentActivityPlayerUnavailable = (
  player: OpponentActivityPlayerStateDto | OpponentActivityUnavailablePlayerDto,
): player is OpponentActivityUnavailablePlayerDto => "unavailable" in player;

/** On-load state of every player in an Arena room, for the live board. */
export const OpponentActivityRoomStateResponseSchema = z.object({
  players: z.array(z.union([OpponentActivityPlayerStateSchema, OpponentActivityUnavailablePlayerSchema])),
});
export type OpponentActivityRoomStateResponseDto = z.infer<typeof OpponentActivityRoomStateResponseSchema>;
